import { useMemo, useEffect, useState, useRef } from "react";
import {
  calculateRoadNetwork,
  roadSegmentsToShaderFormat,
  getRoadNetworkStats,
  type RoadNetwork,
  type RoadSegment,
} from "../utils/clusteringAlgorithm";
import type { PlacedObject } from "../config/types";

interface UseRoadClusteringResult {
  /** 전체 도로 네트워크 정보 */
  network: RoadNetwork;
  /** 셰이더에 전달할 도로 선분 목록 */
  roadSegments: RoadSegment[];
  /** 셰이더 Uniform용 데이터 */
  shaderData: { positions: Float32Array; widths: Float32Array; count: number };
  /** 클러스터 개수 */
  clusterCount: number;
  /** 전체 도로 선분 개수 */
  segmentCount: number;
}

/**
 * 건물 목록을 캐시 키로 변환
 * 건물 ID를 정렬하여 일관된 키 생성
 */
function getBuildingsCacheKey(buildings: PlacedObject[]): string {
  return buildings
    .map((b) => b.id)
    .sort()
    .join(",");
}

/**
 * 도로 클러스터링 훅 (캐싱 최적화 버전)
 *
 * 건물 목록을 받아 도로 네트워크를 계산합니다.
 * - DBSCAN 기반 밀도 클러스터링
 * - Edge-Centric 골목길 생성
 * - 다중 가중치 A* 경로탐색 (밀집 회피, 직선 선호)
 * - MST(최소 신장 트리)로 클러스터 간 연결
 * - 결과 캐싱으로 동일한 건물 조합에 대해 재계산 방지
 *
 * @param buildings 건물 목록
 * @param skipRecalculation 재계산을 건너뛸지 여부 (필터링 모드 등)
 * @returns 도로 네트워크 정보 및 셰이더 데이터
 */
export function useRoadClustering(
  buildings: PlacedObject[],
  skipRecalculation: boolean = false
): UseRoadClusteringResult {
  const [network, setNetwork] = useState<RoadNetwork>({
    clusters: [],
    smallRoads: [],
    bigRoads: [],
    allRoadSegments: [],
  });

  // 캐시 저장소 (모듈 레벨에서 유지)
  const cacheRef = useRef<Map<string, RoadNetwork>>(new Map());
  const lastCacheKeyRef = useRef<string>("");

  // 건물 목록이 변경될 때마다 도로 네트워크 재계산
  useEffect(() => {
    // 재계산을 건너뛰는 경우 (예: 필터링 모드)
    if (skipRecalculation) {
      console.log("[RoadClustering] Skipping recalculation (filter mode)");
      return;
    }

    // 건물이 없으면 빈 네트워크 반환
    if (buildings.length === 0) {
      setNetwork({
        clusters: [],
        smallRoads: [],
        bigRoads: [],
        allRoadSegments: [],
      });
      lastCacheKeyRef.current = "";
      return;
    }

    // 캐시 키 생성
    const cacheKey = getBuildingsCacheKey(buildings);

    // 캐시에 있으면 재사용
    if (cacheRef.current.has(cacheKey)) {
      const cachedNetwork = cacheRef.current.get(cacheKey)!;
      setNetwork(cachedNetwork);
      lastCacheKeyRef.current = cacheKey;
      console.log(
        `[RoadClustering] Using cached result for ${buildings.length} buildings`
      );
      return;
    }

    // 캐시에 없으면 계산
    console.log(
      `[RoadClustering] Calculating network for ${buildings.length} buildings...`
    );
    const startTime = performance.now();
    const newNetwork = calculateRoadNetwork(buildings);
    const endTime = performance.now();

    // 캐시에 저장
    cacheRef.current.set(cacheKey, newNetwork);
    setNetwork(newNetwork);
    lastCacheKeyRef.current = cacheKey;

    const stats = getRoadNetworkStats(newNetwork);
    console.log(
      `[RoadClustering] Calculated in ${(endTime - startTime).toFixed(2)}ms: ` +
        `${stats.totalClusters} clusters (avg size: ${stats.avgClusterSize.toFixed(1)}), ` +
        `${newNetwork.allRoadSegments.length} total roads (small: ${stats.totalSmallRoads}, big: ${stats.totalBigRoads}) ` +
        `from ${buildings.length} buildings`
    );

    // 캐시 크기 제한 (메모리 관리)
    // 최대 10개의 결과만 캐시 유지
    if (cacheRef.current.size > 10) {
      const firstKey = cacheRef.current.keys().next().value;
      cacheRef.current.delete(firstKey);
    }
  }, [buildings, skipRecalculation]);

  // 셰이더 전달용 데이터 생성
  const shaderData = useMemo(() => {
    return roadSegmentsToShaderFormat(network.allRoadSegments);
  }, [network.allRoadSegments]);

  return {
    network,
    roadSegments: network.allRoadSegments,
    shaderData,
    clusterCount: network.clusters.length,
    segmentCount: network.allRoadSegments.length,
  };
}
